import { Typography } from "@mui/material";
import { Stack } from "@mui/system";
import QUESTION_TYPE from "enums/QUESTION_TYPE";
import SCRIPT_TYPE from "enums/SCRIPT_TYPE";
import useGetParticipants from "hooks/api/useGetParticipants";
import useGetScriptById from "hooks/api/useGetScriptById";
import useGetParticipantCompletion from "hooks/useGetParticipantCompletion";
import { useMemo } from "react";
import { getPluralized } from "utils/display";

interface Props {
  scriptId: string;
}

const COUNTED_QUESTION_TYPES = new Set([
  QUESTION_TYPE.MULTIPLE_CHOICE,
  QUESTION_TYPE.NUMERIC,
  QUESTION_TYPE.TEXT,
  QUESTION_TYPE.SINGLE_CHOICE,
  QUESTION_TYPE.VIDEO,
]);

export default function ScriptStats({ scriptId }: Props) {
  const { script } = useGetScriptById({
    scriptId,
    queryParams: { includeQuestions: true },
  });
  const questionCount = useMemo(
    () =>
      script?.questions.filter((question) =>
        COUNTED_QUESTION_TYPES.has(question.type),
      )?.length || 0,
    [script],
  );

  const { participants } = useGetParticipants();
  const participantCount = useMemo(
    () =>
      participants?.filter((participant) =>
        participant.scripts.some(({ id }) => id === scriptId),
      ).length || 0,
    [participants],
  );

  const { moderatedCompleted, mobileCompleted } = useGetParticipantCompletion({
    scriptId,
  });

  const stats: string[] = [
    `${getPluralized("person", participantCount)} invited`,
  ];
  if (script?.type === SCRIPT_TYPE.MODERATED) {
    stats.push(getPluralized("completion", moderatedCompleted));
  }
  if (script?.type === SCRIPT_TYPE.MOBILE) {
    stats.push(getPluralized("question", questionCount));
    stats.push(getPluralized("completion", mobileCompleted));
  }

  return (
    <Stack>
      {stats.map((stat) => (
        <Typography key={stat}>{stat}</Typography>
      ))}
    </Stack>
  );
}
